<template>
  <div class="gl-page">
    <!-- Header -->
    <div class="gl-page-header">
      <div>
        <h1 class="gl-page-title">{{ hasSubscription ? 'My Subscription' : 'Choose Your Plan' }}</h1>
        <div class="gl-breadcrumb">
          <router-link to="/">Home</router-link>
          <span class="gl-breadcrumb-sep">/</span>
          <span>Subscription</span>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="gl-sub-grid">
      <!-- Hero skeleton mirrors the teal plan hero -->
      <div class="gl-skel-hero">
        <div class="gl-skel-hero-top">
          <div class="gl-skel-circle"></div>
          <div style="flex:1;min-width:0">
            <div class="gl-skel-bar" style="width:140px;height:18px;margin-bottom:10px"></div>
            <div class="gl-skel-bar" style="width:64px;height:13px;border-radius:20px"></div>
          </div>
          <div class="gl-skel-bar" style="width:110px;height:34px;border-radius:8px"></div>
        </div>
        <div class="gl-skel-hero-foot">
          <div class="gl-skel-bar" style="width:80%;height:12px;margin-bottom:10px"></div>
          <div style="display:flex;gap:20px">
            <div class="gl-skel-bar" style="width:80px;height:12px"></div>
            <div class="gl-skel-bar" style="width:110px;height:12px"></div>
          </div>
        </div>
      </div>
      <!-- Usage skeleton mirrors the usage card -->
      <div class="gl-skel-usage">
        <div class="gl-skel gl-skel-line" style="width:90px;margin-bottom:22px"></div>
        <div v-for="i in 3" :key="i" class="gl-skel-u-item">
          <div class="gl-skel-u-row">
            <div class="gl-skel gl-skel-line" style="width:55px"></div>
            <div class="gl-skel gl-skel-line" style="width:38px"></div>
          </div>
          <div class="gl-skel" style="height:7px;border-radius:7px"></div>
        </div>
      </div>
    </div>

    <!-- ── HAS SUBSCRIPTION ── -->
    <div v-else-if="hasSubscription">

      <!-- Plan + Usage row -->
      <div class="gl-sub-grid">
        <!-- Current plan hero -->
        <div class="gl-plan-hero">
          <div class="gl-hero-top">
            <div class="gl-hero-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
            </div>
            <div>
              <div class="gl-hero-plan-name">{{ currentPlan?.name }} Plan</div>
              <span class="gl-status-pill" :class="statusPillClass">{{ statusLabel }}</span>
            </div>
            <div class="gl-hero-actions">
              <button v-if="currentSubscription?.on_grace_period" class="gl-btn-resume" @click="resumeSubscription" :disabled="resuming">
                <span v-if="resuming" class="gl-spinner gl-spinner-white"></span>
                <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-4.14"/></svg>
                {{ resuming ? 'Resuming…' : 'Resume Plan' }}
              </button>
              <button v-else class="gl-btn-change" @click="showPlansComparison = true">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
                Change Plan
              </button>
            </div>
          </div>

          <div class="gl-hero-info">
            <p v-if="currentPlan?.description" class="gl-hero-desc">{{ currentPlan.description }}</p>
            <div class="gl-hero-meta-row">
              <div v-if="!currentSubscription?.is_on_trial" class="gl-hero-meta-item">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                <strong>RM{{ Math.round(currentSubscription?.billing_cycle === 'annual' ? currentPlan?.annual_price : currentPlan?.price) }}</strong>
                / {{ currentSubscription?.billing_cycle === 'annual' ? 'year' : 'month' }}
              </div>
              <div class="gl-hero-meta-item" :class="currentSubscription?.is_on_trial ? 'gl-meta-trial' : ''">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                <span v-if="currentSubscription?.is_on_trial">Trial ends <strong>{{ formatDate(currentSubscription?.trial_ends_at) }}</strong></span>
                <span v-else-if="currentSubscription?.on_grace_period" class="gl-meta-danger">Access until <strong>{{ formatDate(currentSubscription?.current_period_end) }}</strong></span>
                <span v-else>Next billing <strong>{{ formatDate(currentSubscription?.current_period_end) }}</strong></span>
              </div>
            </div>
          </div>
        </div>

        <!-- Usage card -->
        <div class="gl-usage-card">
          <h3 class="gl-usage-title">Plan Usage</h3>
          <div class="gl-usage-items">
            <div v-for="(item, key) in usageItems" :key="key" class="gl-usage-item">
              <div class="gl-usage-row">
                <span class="gl-usage-label">{{ item.label }}</span>
                <span class="gl-usage-count">
                  {{ item.used }}
                  <span class="gl-usage-limit">/ {{ item.limit === null ? '∞' : item.limit }}</span>
                </span>
              </div>
              <div class="gl-usage-track">
                <div
                  class="gl-usage-bar"
                  :class="usageBarClass(item.percentage, item.limit)"
                  :style="`width:${item.limit === null ? 100 : item.percentage}%`"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Plan Features -->
      <div class="gl-features-card">
        <div class="gl-features-header">
          <h3 class="gl-features-title">Plan Features</h3>
          <span class="gl-plan-chip">{{ currentPlan?.name }}</span>
        </div>
        <div class="gl-features-grid">
          <div v-for="(enabled, feature) in currentPlan?.features" :key="feature" class="gl-feature-row">
            <div class="gl-feature-icon" :class="enabled ? 'gl-feat-on' : 'gl-feat-off'">
              <svg v-if="enabled" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              <svg v-else width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </div>
            <span class="gl-feature-label" :class="{ 'gl-feat-disabled': !enabled }">{{ formatFeature(feature) }}</span>
          </div>
        </div>
      </div>

      <!-- Cancel zone -->
      <div v-if="currentSubscription?.status === 'active' && !currentSubscription?.on_grace_period && currentPlan?.slug !== 'free'" class="gl-cancel-zone">
        <div>
          <div class="gl-cancel-title">Cancel Subscription</div>
          <div class="gl-cancel-sub">You'll continue to have access until {{ formatDate(currentSubscription?.current_period_end) }}</div>
        </div>
        <button class="gl-btn-cancel-plan" @click="showCancelModal = true">Cancel Plan</button>
      </div>

      <!-- Plans comparison overlay -->
      <div v-if="showPlansComparison" class="gl-modal-backdrop" @click.self="showPlansComparison = false">
        <div class="gl-modal gl-modal-plans">
          <div class="gl-modal-header">
            <h3 class="gl-modal-title">Change Your Plan</h3>
            <button class="gl-modal-close" @click="showPlansComparison = false">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div class="gl-modal-body">
            <div class="gl-billing-toggle">
              <button class="gl-cycle-btn" :class="{ 'gl-cycle-active': billingCycle === 'monthly' }" @click="billingCycle = 'monthly'">Monthly</button>
              <button class="gl-cycle-btn" :class="{ 'gl-cycle-active': billingCycle === 'annual' }" @click="billingCycle = 'annual'">
                Annual
                <span class="gl-save-chip">Save 20%</span>
              </button>
            </div>
            <div class="gl-plans-grid">
              <div v-for="plan in plans" :key="plan.id" class="gl-plan-card" :class="{ 'gl-plan-current': plan.is_current, 'gl-plan-featured': plan.is_featured }">
                <div v-if="plan.is_current" class="gl-plan-badge gl-badge-current">Current</div>
                <div v-else-if="plan.is_featured" class="gl-plan-badge gl-badge-popular">Popular</div>
                <div class="gl-plan-card-name">{{ plan.name }}</div>
                <div class="gl-plan-card-price">
                  <template v-if="plan.is_custom_pricing">
                    <span class="gl-price-custom">Custom</span>
                  </template>
                  <template v-else>
                    <span class="gl-price-currency">RM</span>
                    <span class="gl-price-amount">{{ billingCycle === 'annual' ? Math.round(plan.annual_price / 12) : Math.round(plan.price) }}</span>
                    <span class="gl-price-period">/mo</span>
                  </template>
                </div>
                <div v-if="billingCycle === 'annual' && !plan.is_custom_pricing" class="gl-plan-billed">RM{{ Math.round(plan.annual_price) }}/year</div>
                <div v-else-if="plan.is_custom_pricing" class="gl-plan-billed">Contact us for pricing</div>
                <ul class="gl-plan-limits">
                  <li><strong>{{ plan.limits?.outlets ?? '∞' }}</strong> Outlets</li>
                  <li><strong>{{ plan.limits?.products ?? '∞' }}</strong> Products</li>
                  <li><strong>{{ plan.limits?.staff ?? '∞' }}</strong> Staff</li>
                  <li><strong>{{ plan.limits?.orders_per_month ?? '∞' }}</strong> Orders/mo</li>
                </ul>
                <button v-if="plan.is_current" class="gl-plan-btn gl-plan-btn-current" disabled>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Current Plan
                </button>
                <button v-else-if="plan.is_custom_pricing" class="gl-plan-btn gl-plan-btn-outline" @click="contactSales">Contact Sales</button>
                <button v-else-if="plan.is_upgrade" class="gl-plan-btn gl-plan-btn-primary" @click="changePlan(plan)" :disabled="changingPlan">
                  <span v-if="changingPlan && selectedPlan?.id === plan.id" class="gl-spinner gl-spinner-white"></span>
                  <span v-else>Upgrade</span>
                </button>
                <button v-else class="gl-plan-btn gl-plan-btn-outline" @click="changePlan(plan)" :disabled="changingPlan">Downgrade</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── NO SUBSCRIPTION — PRICING PAGE ── -->
    <div v-else>
      <div class="gl-pricing-header">
        <h2 class="gl-pricing-headline">Simple, Transparent Pricing</h2>
        <p class="gl-pricing-sub">Choose the plan that works best for your business</p>
        <div class="gl-billing-toggle gl-billing-toggle-lg">
          <button class="gl-cycle-btn" :class="{ 'gl-cycle-active': billingCycle === 'monthly' }" @click="billingCycle = 'monthly'">Monthly</button>
          <button class="gl-cycle-btn" :class="{ 'gl-cycle-active': billingCycle === 'annual' }" @click="billingCycle = 'annual'">
            Annual
            <span class="gl-save-chip">Save 20%</span>
          </button>
        </div>
      </div>

      <div class="gl-pricing-grid">
        <div v-for="plan in plans" :key="plan.id" class="gl-pricing-card" :class="{ 'gl-pricing-featured': plan.is_featured }">
          <div v-if="plan.is_featured" class="gl-popular-badge">Most Popular</div>

          <div class="gl-pricing-name">{{ plan.name }}</div>
          <div class="gl-pricing-price-row">
            <template v-if="plan.is_custom_pricing">
              <span class="gl-pricing-custom">Custom</span>
            </template>
            <template v-else>
              <span class="gl-pricing-currency">RM</span>
              <span class="gl-pricing-amount">{{ billingCycle === 'annual' ? Math.round(plan.annual_price / 12) : Math.round(plan.price) }}</span>
              <span class="gl-pricing-period">/mo</span>
            </template>
          </div>
          <div class="gl-pricing-billed">{{ plan.is_custom_pricing ? 'Contact us for pricing' : (billingCycle === 'annual' ? `RM${Math.round(plan.annual_price)}/year` : 'Billed monthly') }}</div>

          <div class="gl-pricing-divider"></div>

          <ul class="gl-pricing-limits">
            <li>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
              <strong>{{ plan.limits?.outlets ?? 'Unlimited' }}</strong> Outlets
            </li>
            <li>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
              <strong>{{ plan.limits?.products ?? 'Unlimited' }}</strong> Products
            </li>
            <li>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              <strong>{{ plan.limits?.staff ?? 'Unlimited' }}</strong> Staff
            </li>
            <li>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
              <strong>{{ plan.limits?.orders_per_month ?? 'Unlimited' }}</strong> Orders/mo
            </li>
          </ul>

          <div class="gl-pricing-divider"></div>

          <ul class="gl-pricing-features">
            <li v-for="(enabled, feature) in plan.features" :key="feature" :class="{ 'gl-feat-row-off': !enabled }">
              <svg v-if="enabled" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#900C27" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#cbd5d3" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              <span :class="{ 'gl-feat-label-off': !enabled }">{{ formatFeature(feature) }}</span>
            </li>
          </ul>

          <div class="gl-pricing-cta">
            <button v-if="plan.is_custom_pricing" class="gl-cta-btn gl-cta-outline" @click="contactSales">Contact Sales</button>
            <button v-else class="gl-cta-btn" :class="plan.is_featured ? 'gl-cta-primary' : 'gl-cta-outline'" @click="selectPlan(plan)" :disabled="subscribing">
              <span v-if="subscribing && selectedPlan?.id === plan.id" class="gl-spinner" :class="plan.is_featured ? 'gl-spinner-white' : ''"></span>
              <span v-else>{{ plan.price === 0 ? 'Start Free' : 'Get Started' }}</span>
            </button>
          </div>
        </div>
      </div>

      <div class="gl-trust-row">
        <span class="gl-trust-chip">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          Secure Payment
        </span>
        <span class="gl-trust-chip">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-4.14"/></svg>
          Cancel Anytime
        </span>
        <span class="gl-trust-chip">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
          24/7 Support
        </span>
      </div>
    </div>

    <!-- Cancel Modal -->
    <div v-if="showCancelModal" class="gl-modal-backdrop" @click.self="showCancelModal = false">
      <div class="gl-modal gl-modal-sm">
        <div class="gl-modal-header">
          <h3 class="gl-modal-title">Cancel Subscription</h3>
          <button class="gl-modal-close" @click="showCancelModal = false">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="gl-modal-body">
          <div class="gl-cancel-icon-wrap">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          </div>
          <p class="gl-cancel-question">Cancel your <strong>{{ currentPlan?.name }}</strong> plan?</p>
          <p class="gl-cancel-note">You'll keep access until <strong>{{ formatDate(currentSubscription?.current_period_end) }}</strong></p>

          <div class="gl-field-group">
            <label class="gl-label">Help us improve <span class="gl-label-optional">optional</span></label>
            <div class="gl-select-wrap">
              <select class="gl-select" v-model="cancelReason">
                <option value="">Select a reason…</option>
                <option value="too_expensive">Too expensive</option>
                <option value="missing_features">Missing features I need</option>
                <option value="switching_competitor">Switching to another service</option>
                <option value="not_using">Not using it enough</option>
                <option value="temporary">Temporary — will come back</option>
                <option value="other">Other</option>
              </select>
              <svg class="gl-select-chevron" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
            </div>
          </div>
          <div v-if="cancelReason === 'other'" class="gl-field-group" style="margin-bottom:0">
            <textarea class="gl-textarea" v-model="cancelReasonText" rows="2" placeholder="Please tell us more…"></textarea>
          </div>
        </div>
        <div class="gl-modal-footer">
          <button class="gl-btn-keep" @click="showCancelModal = false">Keep My Plan</button>
          <button class="gl-btn-danger" @click="cancelSubscription" :disabled="cancelling">
            <span v-if="cancelling" class="gl-spinner gl-spinner-white"></span>
            Cancel Subscription
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { subscriptionApi } from '@/services/subscriptionApi';

export default {
  name: 'SubscriptionPlans',
  data() {
    return {
      loading: true,
      plans: [],
      currentSubscription: null,
      currentPlan: null,
      usage: null,
      billingCycle: 'monthly',
      selectedPlan: null,
      subscribing: false,
      changingPlan: false,
      showPlansComparison: false,
      showCancelModal: false,
      cancelReason: '',
      cancelReasonText: '',
      cancelling: false,
      resuming: false
    };
  },
  computed: {
    hasSubscription() { return this.currentSubscription && this.currentPlan; },
    statusLabel() {
      if (!this.currentSubscription) return '';
      if (this.currentSubscription.is_on_trial) return 'Trial';
      if (this.currentSubscription.on_grace_period) return 'Cancelled';
      const s = this.currentSubscription.status;
      return s.charAt(0).toUpperCase() + s.slice(1);
    },
    statusPillClass() {
      if (!this.currentSubscription) return 'gl-pill-gray';
      if (this.currentSubscription.is_on_trial) return 'gl-pill-amber';
      if (this.currentSubscription.on_grace_period) return 'gl-pill-red';
      const map = { active: 'gl-pill-white', trialing: 'gl-pill-amber', cancelled: 'gl-pill-red', past_due: 'gl-pill-red', expired: 'gl-pill-gray' };
      return map[this.currentSubscription.status] || 'gl-pill-gray';
    },
    usageItems() {
      const l = this.usage?.limits || {};
      return [
        { label: 'Outlets',  used: l.outlets?.used  || 0, limit: l.outlets?.unlimited  ? null : l.outlets?.limit,  percentage: l.outlets?.percentage  || 0 },
        { label: 'Products', used: l.products?.used || 0, limit: l.products?.unlimited ? null : l.products?.limit, percentage: l.products?.percentage || 0 },
        { label: 'Staff',    used: l.staff?.used    || 0, limit: l.staff?.unlimited    ? null : l.staff?.limit,    percentage: l.staff?.percentage    || 0 }
      ];
    }
  },
  async mounted() { await this.loadData(); },
  methods: {
    async loadData() {
      this.loading = true;
      try {
        const plansRes = await subscriptionApi.getPlans();
        this.plans = plansRes.data.data || plansRes.data;
        try {
          const statusRes = await subscriptionApi.getStatus();
          const data = statusRes.data.data;
          this.currentSubscription = data?.subscription;
          this.usage = data?.limits;
          if (this.currentSubscription?.plan) {
            this.currentPlan = this.plans.find(p => p.id === this.currentSubscription.plan.id) || this.currentSubscription.plan;
          }
        } catch { this.currentSubscription = null; this.currentPlan = null; this.usage = null; }
      } catch (e) { console.error('Failed to load plans:', e); }
      finally { this.loading = false; }
    },
    formatDate(d) { if (!d) return '—'; return new Date(d).toLocaleDateString('en-MY', { year: 'numeric', month: 'short', day: 'numeric' }); },
    formatFeature(f) { return f.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '); },
    usageBarClass(pct, limit) {
      if (limit === null) return 'gl-bar-teal';
      if (pct >= 90) return 'gl-bar-red';
      if (pct >= 70) return 'gl-bar-amber';
      return 'gl-bar-teal';
    },
    async selectPlan(plan) {
      const user = JSON.parse(localStorage.getItem('getlokal_admin_user') || '{}');
      if (!user.company_id) { this.$router.push({ path: '/auth/company-register', query: { plan_id: plan.id } }); return; }
      this.selectedPlan = plan; this.subscribing = true;
      try {
        await subscriptionApi.subscribe({ plan_id: plan.id, billing_cycle: this.billingCycle });
        await this.loadData();
      } catch (e) { console.error(e); }
      finally { this.subscribing = false; this.selectedPlan = null; }
    },
    async changePlan(plan) {
      this.selectedPlan = plan; this.changingPlan = true;
      try {
        await subscriptionApi.changePlan({ plan_id: plan.id, billing_cycle: this.billingCycle, immediately: true });
        this.showPlansComparison = false;
        await this.loadData();
      } catch (e) { console.error(e); }
      finally { this.changingPlan = false; this.selectedPlan = null; }
    },
    async cancelSubscription() {
      this.cancelling = true;
      try {
        const reason = this.cancelReason === 'other' ? this.cancelReasonText : this.cancelReason;
        await subscriptionApi.cancel({ reason });
        this.showCancelModal = false; this.cancelReason = ''; this.cancelReasonText = '';
        await this.loadData();
      } catch (e) { console.error(e); }
      finally { this.cancelling = false; }
    },
    async resumeSubscription() {
      this.resuming = true;
      try { await subscriptionApi.resume(); await this.loadData(); }
      catch (e) { console.error(e); }
      finally { this.resuming = false; }
    },
    contactSales() { window.open('mailto:sales@getlokal.my?subject=Enterprise Plan Inquiry', '_blank'); }
  }
};
</script>

<style scoped>
/* ── Page ── */
.gl-page { padding: 28px 32px; max-width: 1200px; }
.gl-page-header { margin-bottom: 28px; }
.gl-page-title { font-size: 22px; font-weight: 700; color: #1a2e2b; margin: 0 0 4px; letter-spacing: -0.3px; }
.gl-breadcrumb { font-size: 12px; color: #8a9e9a; display: flex; align-items: center; gap: 6px; }
.gl-breadcrumb a { color: #8a9e9a; text-decoration: none; }
.gl-breadcrumb a:hover { color: #900C27; }
.gl-breadcrumb-sep { opacity: .5; }

/* ── Plan + Usage grid ── */
.gl-sub-grid { display: grid; grid-template-columns: 1fr 300px; gap: 20px; margin-bottom: 20px; align-items: stretch; }
.gl-usage-card { display: flex; flex-direction: column; }

/* ── Plan hero card ── */
.gl-plan-hero {
  background: linear-gradient(135deg, #1a4a40 0%, #900C27 60%, #2d8474 100%);
  border-radius: 16px; padding: 28px; color: #fff;
}
.gl-hero-top { display: flex; align-items: flex-start; gap: 16px; margin-bottom: 20px; flex-wrap: wrap; }
.gl-hero-icon { width: 48px; height: 48px; border-radius: 12px; background: rgba(255,255,255,.18); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.gl-hero-plan-name { font-size: 22px; font-weight: 800; letter-spacing: -0.5px; margin-bottom: 6px; }
.gl-status-pill { display: inline-block; font-size: 11px; font-weight: 700; padding: 3px 10px; border-radius: 20px; letter-spacing: .3px; }
.gl-pill-white   { background: rgba(255,255,255,.22); color: #fff; border: 1px solid rgba(255,255,255,.5); }
.gl-pill-amber   { background: #fef3c7; color: #92400e; }
.gl-pill-red     { background: #fce7e7; color: #dc2626; }
.gl-pill-gray    { background: rgba(255,255,255,.15); color: rgba(255,255,255,.8); }

.gl-hero-actions { margin-left: auto; display: flex; gap: 8px; align-items: center; flex-shrink: 0; }
.gl-btn-change {
  display: inline-flex; align-items: center; gap: 7px; padding: 9px 16px;
  font-size: 13px; font-weight: 600; background: rgba(255,255,255,.2); color: #fff;
  border: 1.5px solid rgba(255,255,255,.35); border-radius: 8px; cursor: pointer;
  transition: background .15s; text-decoration: none;
}
.gl-btn-change:hover { background: rgba(255,255,255,.28); }
.gl-btn-resume {
  display: inline-flex; align-items: center; gap: 7px; padding: 9px 16px;
  font-size: 13px; font-weight: 600; background: #fff; color: #900C27;
  border: none; border-radius: 8px; cursor: pointer; transition: background .15s;
}
.gl-btn-resume:hover:not(:disabled) { background: #f0f8f5; }
.gl-btn-resume:disabled { opacity: .6; cursor: not-allowed; }

.gl-hero-desc { font-size: 13.5px; color: rgba(255,255,255,.75); margin: 0 0 14px; }
.gl-hero-meta-row { display: flex; flex-wrap: wrap; gap: 18px; }
.gl-hero-meta-item { display: flex; align-items: center; gap: 7px; font-size: 13px; color: rgba(255,255,255,.8); }
.gl-hero-meta-item strong { color: #fff; }
.gl-meta-trial { color: #fef3c7 !important; }
.gl-meta-danger { color: #fca5a5; }

/* ── Usage card ── */
.gl-usage-card { background: #fff; border: 1px solid #eaedec; border-radius: 16px; padding: 22px; }
.gl-usage-title { font-size: 14px; font-weight: 700; color: #1a2e2b; margin: 0 0 18px; }
.gl-usage-items { display: flex; flex-direction: column; gap: 0; flex: 1; justify-content: space-around; }
.gl-usage-item { }
.gl-usage-row { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 6px; }
.gl-usage-label { font-size: 13px; color: #5a7470; font-weight: 500; }
.gl-usage-count { font-size: 13px; font-weight: 700; color: #1a2e2b; }
.gl-usage-limit { font-size: 11.5px; font-weight: 400; color: #8a9e9a; }
.gl-usage-track { height: 7px; background: #eef0ef; border-radius: 7px; overflow: hidden; }
.gl-usage-bar { height: 100%; border-radius: 7px; transition: width .4s ease; }
.gl-bar-teal  { background: linear-gradient(90deg, #900C27, #3d9e8c); }
.gl-bar-amber { background: linear-gradient(90deg, #d97706, #f59e0b); }
.gl-bar-red   { background: linear-gradient(90deg, #dc2626, #ef4444); }

/* ── Features card ── */
.gl-features-card { background: #fff; border: 1px solid #eaedec; border-radius: 14px; overflow: hidden; margin-bottom: 16px; }
.gl-features-header { display: flex; align-items: center; justify-content: space-between; padding: 18px 22px 14px; border-bottom: 1px solid #f0f3f2; }
.gl-features-title { font-size: 14px; font-weight: 700; color: #1a2e2b; margin: 0; }
.gl-plan-chip { font-size: 11.5px; font-weight: 700; padding: 3px 10px; background: #e0f0ec; color: #1a5248; border-radius: 20px; }
.gl-features-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0; padding: 4px 0; }
.gl-feature-row { display: flex; align-items: center; gap: 10px; padding: 11px 22px; }
.gl-feature-icon { width: 20px; height: 20px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.gl-feat-on { background: #e0f0ec; color: #900C27; }
.gl-feat-off { background: #f0f3f2; color: #b0c4c0; }
.gl-feature-label { font-size: 13px; color: #1a2e2b; font-weight: 500; }
.gl-feat-disabled { color: #b0c4c0; }

/* ── Cancel zone ── */
.gl-cancel-zone { display: flex; align-items: center; justify-content: space-between; background: #fff; border: 1px solid #f0dede; border-radius: 12px; padding: 16px 20px; gap: 16px; flex-wrap: wrap; }
.gl-cancel-title { font-size: 13.5px; font-weight: 600; color: #6b7f7b; margin-bottom: 3px; }
.gl-cancel-sub { font-size: 12px; color: #a0b5b0; }
.gl-btn-cancel-plan { padding: 8px 16px; font-size: 12.5px; font-weight: 500; color: #dc2626; background: #fff; border: 1.5px solid #f0c0c0; border-radius: 8px; cursor: pointer; transition: background .12s; white-space: nowrap; }
.gl-btn-cancel-plan:hover { background: #fef2f2; }

/* ── Billing toggle ── */
.gl-billing-toggle { display: inline-flex; background: #f0f3f2; border-radius: 10px; padding: 3px; gap: 2px; }
.gl-billing-toggle-lg { margin-top: 20px; }
.gl-cycle-btn { display: inline-flex; align-items: center; gap: 7px; padding: 8px 20px; font-size: 13px; font-weight: 500; color: #5a7470; background: none; border: none; border-radius: 8px; cursor: pointer; transition: all .15s; }
.gl-cycle-active { background: #fff; color: #1a2e2b; font-weight: 600; box-shadow: 0 1px 4px rgba(0,0,0,.1); }
.gl-save-chip { font-size: 10.5px; font-weight: 700; padding: 2px 7px; background: #e0f0ec; color: #1a5248; border-radius: 20px; }

/* ── Plans comparison grid (in modal) ── */
.gl-plans-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 14px; margin-top: 20px; }
.gl-plan-card { background: #f8faf9; border: 2px solid #eaedec; border-radius: 12px; padding: 18px; text-align: center; position: relative; transition: border-color .15s; display: flex; flex-direction: column; }
.gl-plan-card:hover { border-color: #900C27; }
.gl-plan-current { border-color: #16a34a; background: #f0fdf4; }
.gl-plan-featured { border-color: #900C27; }
.gl-plan-badge { position: absolute; top: -10px; left: 50%; transform: translateX(-50%); font-size: 10px; font-weight: 700; padding: 3px 10px; border-radius: 20px; white-space: nowrap; }
.gl-badge-current { background: #16a34a; color: #fff; }
.gl-badge-popular { background: #900C27; color: #fff; }
.gl-plan-card-name { font-size: 14px; font-weight: 700; color: #1a2e2b; margin-bottom: 10px; }
.gl-plan-card-price { display: flex; align-items: baseline; justify-content: center; gap: 2px; }
.gl-price-currency { font-size: 14px; color: #5a7470; font-weight: 500; }
.gl-price-amount { font-size: 28px; font-weight: 800; color: #900C27; line-height: 1; }
.gl-price-period { font-size: 12px; color: #8a9e9a; }
.gl-plan-billed { font-size: 11px; color: #8a9e9a; margin-top: 3px; min-height: 16px; }
.gl-price-custom { font-size: 22px; font-weight: 800; color: #900C27; line-height: 1; }
.gl-pricing-custom { font-size: 38px; font-weight: 900; color: #900C27; line-height: 1; letter-spacing: -1px; }
.gl-plan-limits { list-style: none; padding: 0; margin: 14px 0; text-align: left; font-size: 12px; color: #5a7470; display: flex; flex-direction: column; gap: 4px; }
.gl-plan-limits li strong { color: #1a2e2b; }
.gl-plan-limits { flex: 1; }
.gl-plan-btn { width: 100%; padding: 9px; font-size: 12.5px; font-weight: 600; border-radius: 8px; cursor: pointer; border: 2px solid; transition: all .15s; margin-top: auto; display: flex; align-items: center; justify-content: center; gap: 6px; }
.gl-plan-btn-primary { background: #900C27; color: #fff; border-color: #900C27; }
.gl-plan-btn-primary:hover:not(:disabled) { background: #1a5248; }
.gl-plan-btn-outline { background: #fff; color: #900C27; border-color: #900C27; }
.gl-plan-btn-outline:hover:not(:disabled) { background: #f0f8f5; }
.gl-plan-btn-current { background: #f0fdf4; color: #16a34a; border-color: #16a34a; cursor: not-allowed; }
.gl-plan-btn:disabled { opacity: .55; cursor: not-allowed; }

/* ── Pricing page (no subscription) ── */
.gl-pricing-header { text-align: center; margin-bottom: 40px; }
.gl-pricing-headline { font-size: 28px; font-weight: 800; color: #1a2e2b; letter-spacing: -0.5px; margin: 0 0 8px; }
.gl-pricing-sub { font-size: 15px; color: #6b7f7b; margin: 0; }
.gl-pricing-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 20px; margin-bottom: 40px; }
.gl-pricing-card {
  background: #fff; border: 2px solid #eaedec; border-radius: 18px; padding: 28px 24px;
  display: flex; flex-direction: column; position: relative;
  transition: transform .2s, box-shadow .2s; overflow: visible;
}
.gl-pricing-card:hover { transform: translateY(-4px); box-shadow: 0 16px 40px rgba(0,0,0,.1); }
.gl-pricing-featured { border-color: #900C27; box-shadow: 0 8px 24px rgba(144,12,39,.15); }
.gl-popular-badge { position: absolute; top: -13px; left: 50%; transform: translateX(-50%); background: #900C27; color: #fff; padding: 5px 16px; border-radius: 20px; font-size: 11px; font-weight: 700; white-space: nowrap; }
.gl-pricing-name { font-size: 13px; font-weight: 700; color: #6b8a85; text-transform: uppercase; letter-spacing: .8px; margin-bottom: 16px; }
.gl-pricing-price-row { display: flex; align-items: baseline; gap: 2px; }
.gl-pricing-currency { font-size: 16px; font-weight: 600; color: #5a7470; }
.gl-pricing-amount { font-size: 46px; font-weight: 900; color: #1a2e2b; line-height: 1; letter-spacing: -2px; }
.gl-pricing-period { font-size: 14px; color: #8a9e9a; }
.gl-pricing-billed { font-size: 12px; color: #8a9e9a; margin-top: 5px; margin-bottom: 0; }
.gl-pricing-divider { height: 1px; background: #eef0ef; margin: 20px 0; }
.gl-pricing-limits { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 9px; }
.gl-pricing-limits li { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #4a6360; }
.gl-pricing-limits li svg { color: #900C27; flex-shrink: 0; }
.gl-pricing-limits li strong { color: #1a2e2b; }
.gl-pricing-features { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 8px; flex: 1; }
.gl-pricing-features li { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #1a2e2b; }
.gl-feat-row-off { }
.gl-feat-label-off { color: #b0c4c0; }
.gl-pricing-cta { margin-top: 20px; }
.gl-cta-btn { width: 100%; padding: 12px; font-size: 14px; font-weight: 700; border-radius: 10px; cursor: pointer; border: 2px solid; transition: all .15s; display: flex; align-items: center; justify-content: center; gap: 7px; }
.gl-cta-primary { background: #900C27; color: #fff; border-color: #900C27; }
.gl-cta-primary:hover:not(:disabled) { background: #1a5248; }
.gl-cta-outline { background: #fff; color: #900C27; border-color: #900C27; }
.gl-cta-outline:hover:not(:disabled) { background: #f0f8f5; }
.gl-cta-btn:disabled { opacity: .55; cursor: not-allowed; }

.gl-trust-row { display: flex; justify-content: center; gap: 12px; flex-wrap: wrap; margin-top: 8px; }
.gl-trust-chip { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; background: #f0f5f4; color: #5a7470; border-radius: 20px; font-size: 12.5px; font-weight: 500; }

/* ── Skeleton ── */
.gl-skel-hero { background: linear-gradient(135deg, #1a4a40 0%, #900C27 60%, #2d8474 100%); border-radius: 16px; padding: 28px; display: flex; flex-direction: column; min-height: 200px; }
.gl-skel-hero-top { display: flex; align-items: flex-start; gap: 16px; margin-bottom: 20px; flex-wrap: wrap; }
.gl-skel-hero-foot { margin-top: auto; }
.gl-skel-circle { width: 48px; height: 48px; border-radius: 12px; flex-shrink: 0; background: linear-gradient(90deg, rgba(255,255,255,.1) 25%, rgba(255,255,255,.22) 50%, rgba(255,255,255,.1) 75%); background-size: 200% 100%; animation: gl-shimmer 1.6s infinite; }
.gl-skel-bar { border-radius: 6px; background: linear-gradient(90deg, rgba(255,255,255,.1) 25%, rgba(255,255,255,.22) 50%, rgba(255,255,255,.1) 75%); background-size: 200% 100%; animation: gl-shimmer 1.6s infinite; }
.gl-skel-usage { background: #fff; border: 1px solid #eaedec; border-radius: 16px; padding: 22px; }
.gl-skel { background: linear-gradient(90deg, #f0f2f1 25%, #e5e8e7 50%, #f0f2f1 75%); background-size: 200% 100%; animation: gl-shimmer 1.4s infinite; border-radius: 6px; }
.gl-skel-line { height: 13px; }
.gl-skel-u-item { margin-bottom: 18px; }
.gl-skel-u-row { display: flex; justify-content: space-between; margin-bottom: 7px; }
@keyframes gl-shimmer { to { background-position: -200% 0; } }

/* ── Mobile ── */
@media (max-width: 767px) {
  .gl-page { padding: 14px 16px 40px; }
  .gl-page-title { font-size: 18px; }
  .gl-breadcrumb { display: none; }
  .gl-page-header { margin-bottom: 14px; }

  /* Grid: stack vertically */
  .gl-sub-grid { grid-template-columns: 1fr; }

  /* Hero card */
  .gl-plan-hero { padding: 20px; }
  .gl-hero-icon { width: 40px; height: 40px; }
  .gl-hero-plan-name { font-size: 18px; }
  .gl-hero-actions { margin-left: 0; width: 100%; }
  .gl-btn-change, .gl-btn-resume { width: 100%; justify-content: center; }
  .gl-hero-meta-row { gap: 10px; }

  /* Hero skeleton on mobile */
  .gl-skel-hero { padding: 20px; min-height: 170px; }
  .gl-skel-hero-top { gap: 12px; }
  .gl-skel-circle { width: 40px; height: 40px; }

  /* Features: 2-col */
  .gl-features-grid { grid-template-columns: repeat(2, 1fr); }
  .gl-feature-row { padding: 10px 14px; }
  .gl-features-header { padding: 14px 16px 12px; }

  /* Cancel zone: stack */
  .gl-cancel-zone { flex-direction: column; align-items: stretch; gap: 12px; padding: 14px 16px; }
  .gl-btn-cancel-plan { text-align: center; }

  /* Pricing header */
  .gl-pricing-header { margin-bottom: 28px; }
  .gl-pricing-headline { font-size: 22px; }
  .gl-pricing-sub { font-size: 13.5px; }

  /* Pricing grid: single col */
  .gl-pricing-grid { grid-template-columns: 1fr; }
  .gl-pricing-card { padding: 22px 18px; }
  .gl-pricing-amount { font-size: 38px; letter-spacing: -1px; }
  .gl-pricing-featured { margin-top: 14px; }

  /* Plans comparison in Change Plan modal */
  .gl-plans-grid { grid-template-columns: 1fr; }

  /* Trust row */
  .gl-trust-row { gap: 8px; }
  .gl-trust-chip { font-size: 12px; padding: 7px 12px; }

  /* Modal */
  .gl-modal-backdrop { padding: 68px 16px 16px; align-items: center; }
  .gl-modal { border-radius: 16px; max-height: calc(100vh - 84px); }
  .gl-modal-header { padding: 16px 16px 14px; }
  .gl-modal-body { padding: 14px 16px; }
  .gl-modal-footer { padding: 14px 16px; flex-wrap: wrap; }
  .gl-btn-keep, .gl-btn-danger { flex: 1; justify-content: center; white-space: nowrap; }
}

/* ── Cancel modal ── */
.gl-cancel-icon-wrap { width: 52px; height: 52px; border-radius: 50%; background: #fef3c7; color: #d97706; display: flex; align-items: center; justify-content: center; margin: 0 auto 16px; }
.gl-cancel-question { text-align: center; font-size: 14px; color: #1a2e2b; margin-bottom: 6px; }
.gl-cancel-note { text-align: center; font-size: 12.5px; color: #8a9e9a; margin-bottom: 20px; }

/* ── Modal ── */
.gl-modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,.45); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 24px; }
.gl-modal { background: #fff; border-radius: 16px; width: 100%; box-shadow: 0 24px 60px rgba(0,0,0,.18); animation: gl-modal-in .2s ease; max-height: 90vh; overflow-y: auto; }
.gl-modal-plans { max-width: 900px; }
.gl-modal-sm { max-width: 440px; }
@keyframes gl-modal-in { from { opacity: 0; transform: scale(.96) translateY(8px); } to { opacity: 1; } }
.gl-modal-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px 16px; border-bottom: 1px solid #eef0ef; position: sticky; top: 0; background: #fff; z-index: 1; }
.gl-modal-title { font-size: 16px; font-weight: 700; color: #1a2e2b; margin: 0; }
.gl-modal-close { display: flex; align-items: center; justify-content: center; width: 30px; height: 30px; border-radius: 8px; background: #f8faf9; border: none; color: #6b7f7b; cursor: pointer; }
.gl-modal-close:hover { background: #eef0ef; }
.gl-modal-body { padding: 20px 24px; }
.gl-modal-footer { display: flex; gap: 10px; justify-content: flex-end; padding: 16px 24px; border-top: 1px solid #eef0ef; }

/* ── Buttons ── */
.gl-btn-keep { padding: 10px 20px; font-size: 13.5px; font-weight: 600; background: #900C27; color: #fff; border: none; border-radius: 8px; cursor: pointer; }
.gl-btn-keep:hover { background: #1a5248; }
.gl-btn-danger { display: inline-flex; align-items: center; gap: 7px; padding: 10px 20px; font-size: 13.5px; font-weight: 600; background: #dc2626; color: #fff; border: none; border-radius: 8px; cursor: pointer; }
.gl-btn-danger:hover:not(:disabled) { background: #b91c1c; }
.gl-btn-danger:disabled { opacity: .55; cursor: not-allowed; }

/* ── Fields ── */
.gl-field-group { margin-bottom: 14px; }
.gl-label { display: block; font-size: 12px; font-weight: 600; color: #3d5450; margin-bottom: 7px; text-transform: uppercase; letter-spacing: .2px; }
.gl-label-optional { font-size: 11px; font-weight: 400; color: #9db5b0; text-transform: none; margin-left: 4px; }
.gl-select-wrap { position: relative; }
.gl-select { appearance: none; width: 100%; padding: 9px 30px 9px 12px; font-size: 13.5px; color: #1a2e2b; background: #f8faf9; border: 1.5px solid #e0e9e7; border-radius: 8px; outline: none; cursor: pointer; font-family: inherit; box-sizing: border-box; }
.gl-select:focus { border-color: #900C27; }
.gl-select-chevron { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); pointer-events: none; color: #8a9e9a; }
.gl-textarea { width: 100%; padding: 9px 12px; font-size: 13.5px; color: #1a2e2b; background: #f8faf9; border: 1.5px solid #e0e9e7; border-radius: 8px; outline: none; resize: vertical; font-family: inherit; box-sizing: border-box; }
.gl-textarea:focus { border-color: #900C27; }

/* ── Spinner ── */
.gl-spinner { display: inline-block; width: 13px; height: 13px; border: 2px solid rgba(144,12,39,.25); border-top-color: #900C27; border-radius: 50%; animation: gl-spin .65s linear infinite; }
.gl-spinner-white { border-color: rgba(255,255,255,.3); border-top-color: #fff; }
@keyframes gl-spin { to { transform: rotate(360deg); } }
</style>

<style>
body.dark-only .gl-page-title { color: #e0eded; }
body.dark-only .gl-breadcrumb, body.dark-only .gl-breadcrumb a { color: #5a7470; }
body.dark-only .gl-usage-card { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-usage-title { color: #e0eded; }
body.dark-only .gl-usage-label { color: #7aada5; }
body.dark-only .gl-usage-count { color: #e0eded; }
body.dark-only .gl-usage-track { background: #2a3d3a; }
body.dark-only .gl-features-card { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-features-header { border-bottom-color: #2a3d3a; }
body.dark-only .gl-features-title { color: #e0eded; }
body.dark-only .gl-feature-label { color: #c0d4d0; }
body.dark-only .gl-cancel-zone { background: #1e2d2b; border-color: #3a2828; }
body.dark-only .gl-cancel-title { color: #7aada5; }
body.dark-only .gl-pricing-headline { color: #e0eded; }
body.dark-only .gl-billing-toggle { background: #16211f; }
body.dark-only .gl-cycle-active { background: #1e2d2b; color: #e0eded; }
body.dark-only .gl-pricing-card { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-pricing-featured { border-color: #900C27; }
body.dark-only .gl-pricing-name { color: #7aada5; }
body.dark-only .gl-pricing-amount { color: #e0eded; }
body.dark-only .gl-pricing-divider { background: #2a3d3a; }
body.dark-only .gl-pricing-features li { color: #c0d4d0; }
body.dark-only .gl-cta-outline { background: #1e2d2b; }
body.dark-only .gl-plan-card { background: #16211f; border-color: #2a3d3a; }
body.dark-only .gl-plan-card-name { color: #e0eded; }
body.dark-only .gl-modal { background: #1e2d2b; }
body.dark-only .gl-modal-header { border-bottom-color: #2a3d3a; background: #1e2d2b; }
body.dark-only .gl-modal-title { color: #e0eded; }
body.dark-only .gl-modal-close { background: #16211f; color: #7aada5; }
body.dark-only .gl-modal-footer { border-top-color: #2a3d3a; }
body.dark-only .gl-select { background: #16211f; border-color: #2a3d3a; color: #e0eded; }
body.dark-only .gl-textarea { background: #16211f; border-color: #2a3d3a; color: #e0eded; }
body.dark-only .gl-cancel-question { color: #e0eded; }
body.dark-only .gl-trust-chip { background: #1e2d2b; color: #7aada5; }
body.dark-only .gl-skel-usage { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-skel { background: linear-gradient(90deg, #243432 25%, #2a3d3a 50%, #243432 75%); background-size: 200% 100%; }
</style>
